import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export interface IHandoffMetricOptionProps {
  name: string;
  /** Pre-formatted by the caller — the slide-out formats counts, rates and scores upstream. */
  total: string;
  eligible: string;
  /** Omitted for a metric the suite could not score; the stat is then left out entirely. */
  confidence?: string;
  selected: boolean;
  onToggle: () => void;
  /**
   * Present when the metric cannot be handed off. The row stays in the list and states the reason:
   * the FR requires an unavailable metric to be visible and explained, not filtered out.
   */
  unavailableReason?: string;
  className?: string;
}

const Option = styled.button<{ $selected: boolean; $unavailable: boolean }>`
  display: block;
  width: 100%;
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? tokens.color['accent-purple'] : tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
  background: ${({ $selected }) =>
    $selected ? tokens.color['surface-card-alt'] : tokens.color['surface-card']};
  cursor: ${({ $unavailable }) => ($unavailable ? 'default' : 'pointer')};
  opacity: ${({ $unavailable }) => ($unavailable ? 0.6 : 1)};
  text-align: left;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const NameRow = styled.span`
  display: flex;
  align-items: center;
`;

const Marker = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  margin-right: ${tokens.space['12']};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? tokens.color['accent-purple'] : tokens.color['border-strong']};
  border-radius: ${tokens.radius.xs};
  background: ${({ $selected }) => ($selected ? tokens.color['accent-purple'] : 'transparent')};
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  line-height: 1;
`;

const Name = styled.span`
  min-width: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['card-title-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['card-title-line']};
`;

const Stats = styled.span`
  display: flex;
  flex-wrap: wrap;

  > * + * {
    margin-left: ${tokens.space['24']};
  }
`;

const StatCell = styled.span`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
  text-transform: uppercase;
`;

const StatValue = styled.span`
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Reason = styled.span`
  display: block;
  color: ${tokens.color.warning};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Stat: React.FC<{ label: string; value: string; testId: string }> = ({
  label,
  value,
  testId
}) => (
  <StatCell data-testid={testId}>
    <StatLabel>{label}</StatLabel>
    <StatValue>{value}</StatValue>
  </StatCell>
);

const HandoffMetricOption: React.FC<IHandoffMetricOptionProps> = ({
  name,
  total,
  eligible,
  confidence,
  selected,
  onToggle,
  unavailableReason,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const unavailable = Boolean(unavailableReason);

  return (
    // `aria-disabled` rather than `disabled`: a natively disabled control drops out of the tab
    // order, which would hide the unavailability reason from anyone reading by keyboard.
    <Option
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-disabled={unavailable}
      onClick={unavailable ? undefined : onToggle}
      className={className}
      $selected={selected}
      $unavailable={unavailable}
      data-selected={selected}
      data-unavailable={unavailable}
      data-testid="handoff-metric-option"
    >
      <NameRow>
        {/* Selection is otherwise carried by border hue alone (WCAG 1.4.1). */}
        <Marker $selected={selected} aria-hidden="true" data-testid="handoff-metric-option-marker">
          {selected ? <>&#10003;</> : null}
        </Marker>
        <Name data-testid="handoff-metric-option-name">{name}</Name>
      </NameRow>

      <Stats>
        <Stat
          label={t(`${TCustomerInsights}.Handoff.MetricTotal`)}
          value={total}
          testId="handoff-metric-option-total"
        />
        <Stat
          label={t(`${TCustomerInsights}.Handoff.MetricEligible`)}
          value={eligible}
          testId="handoff-metric-option-eligible"
        />
        {confidence ? (
          <Stat
            label={t(`${TCustomerInsights}.Handoff.MetricConfidence`)}
            value={confidence}
            testId="handoff-metric-option-confidence"
          />
        ) : null}
      </Stats>

      {unavailable ? (
        <Reason data-testid="handoff-metric-option-reason">
          {t(`${TCustomerInsights}.Handoff.MetricUnavailable`, { reason: unavailableReason })}
        </Reason>
      ) : null}
    </Option>
  );
};

export default HandoffMetricOption;
